import type { LLMMessage } from '@/IsekaiRPG/modules/llm/llmTypes';
import {
  GardenFlowerProfileRegistry,
  type GardenCorePromptMode,
  type GardenFlowerContextStrategy,
  type GardenFlowerId,
  type GardenFlowerPromptBlock,
  type GardenFlowerTask,
  type GardenPromptBlockKey,
} from './GardenFlowerProfiles';

export type {
  GardenCorePromptMode,
  GardenFlowerContextStrategy,
  GardenFlowerId,
  GardenFlowerPromptBlock,
  GardenFlowerTask,
  GardenFlowerTaskProfile,
  GardenPromptBlockKey,
  GardenPromptBlockProfile,
} from './GardenFlowerProfiles';

export interface GardenFlowerResolution {
  task: GardenFlowerTask;
  flowerId: GardenFlowerId;
  flowerName: string;
  profileName: string;
  responsibility: string;
  contextStrategy: GardenFlowerContextStrategy;
  blocks: GardenFlowerPromptBlock[];
  prompt: string;
  blockKeys: GardenPromptBlockKey[];
  markerIdentifier: string | null;
  promptRole: LLMMessage['role'];
  corePromptMode: GardenCorePromptMode;
  isPrimaryFlower: boolean;
  allowedCollaborationTargets: GardenFlowerId[];
}

export interface GardenFlowerSnapshotMeta {
  task: GardenFlowerTask;
  flowerId: GardenFlowerId;
  flowerName: string;
  profileName: string;
  responsibility: string;
  contextStrategy: GardenFlowerContextStrategy;
  blockKeys: GardenPromptBlockKey[];
  blockLabels: string[];
  markerIdentifier: string | null;
  promptRole: LLMMessage['role'];
  corePromptMode: GardenCorePromptMode;
  isPrimaryFlower: boolean;
  allowedCollaborationTargets: GardenFlowerId[];
}

export class GardenFlowerResolver {
  static resolve(task: GardenFlowerTask): GardenFlowerResolution {
    const profile = GardenFlowerProfileRegistry.getTaskProfile(task);
    const blocks = GardenFlowerProfileRegistry.resolveTaskBlocks(task);

    return {
      task,
      flowerId: profile.flowerId,
      flowerName: profile.flowerName,
      profileName: profile.profileName,
      responsibility: profile.responsibility,
      contextStrategy: profile.contextStrategy,
      blocks,
      prompt: blocks.map(block => block.content).join('\n\n'),
      blockKeys: [...profile.blockKeys],
      markerIdentifier: profile.markerIdentifier,
      promptRole: profile.promptRole,
      corePromptMode: profile.corePromptMode,
      isPrimaryFlower: profile.isPrimaryFlower,
      allowedCollaborationTargets: [...profile.allowedCollaborationTargets],
    };
  }

  static resolvePrompt(task: GardenFlowerTask): string {
    return this.resolve(task).prompt;
  }

  static resolveCorePrompt(): string {
    return GardenFlowerProfileRegistry.getCoreBlockProfile().content;
  }

  static resolvePromptWithCore(task: GardenFlowerTask): string {
    return [this.resolveCorePrompt(), this.resolvePrompt(task)].filter(Boolean).join('\n\n');
  }

  static detectFromMessages(
    messages: readonly Pick<LLMMessage, 'role' | 'content'>[],
    options: { includeProtocolOnly?: boolean } = {},
  ): GardenFlowerSnapshotMeta | null {
    const systemContents = messages
      .filter(message => message.role === 'system')
      .map(message => message.content.trim())
      .filter(Boolean);

    return this.detectFromContents(systemContents, options);
  }

  static detectFromMarkerPayloads(
    markerPayloads?: Record<string, string[]>,
    options: { includeProtocolOnly?: boolean } = {},
  ): GardenFlowerSnapshotMeta | null {
    const contents = Object.values(markerPayloads ?? {})
      .flatMap(items => items)
      .map(content => content.trim())
      .filter(Boolean);

    return this.detectFromContents(contents, options);
  }

  private static detectFromContents(
    systemContents: string[],
    options: { includeProtocolOnly?: boolean } = {},
  ): GardenFlowerSnapshotMeta | null {
    if (systemContents.length === 0) {
      return null;
    }

    const includeProtocolOnly = options.includeProtocolOnly ?? false;

    const profile = GardenFlowerProfileRegistry.findTaskProfileByMarkers(systemContents, { includeProtocolOnly });
    if (profile) {
      const blocks = GardenFlowerProfileRegistry.resolveTaskBlocks(profile.task);
      return {
        task: profile.task,
        flowerId: profile.flowerId,
        flowerName: profile.flowerName,
        profileName: profile.profileName,
        responsibility: profile.responsibility,
        contextStrategy: profile.contextStrategy,
        blockKeys: [...profile.blockKeys],
        blockLabels: blocks.map(block => block.label),
        markerIdentifier: profile.markerIdentifier,
        promptRole: profile.promptRole,
        corePromptMode: profile.corePromptMode,
        isPrimaryFlower: profile.isPrimaryFlower,
        allowedCollaborationTargets: [...profile.allowedCollaborationTargets],
      };
    }

    return null;
  }
}
